import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CommonBanner, Footer, Header } from "../../components";
import { get_campus_timetable } from "../../apis/Branches";
import { ENROL_URL } from "../../constants/journey";
import { C, FONT, container } from "../../constants/mq";

// One campus: where it is, how to reach it, and what runs there this term.
//
// The timetable is the classes the campus has set up in the admin, read back
// live, the same source as each course page's timetable. Nothing is
// published by hand, so it cannot go stale. A campus with no classes in the
// new system yet (most primary campuses still run on CS Online School) gets a
// note and its phone number instead of an empty table.

const s = {
  section: { background: "#ffffff", padding: "48px 0 56px", fontFamily: FONT },
  sectionAlt: { background: C.sand, padding: "40px 0", fontFamily: FONT },
  container: { ...container },
  info: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 18,
    alignItems: "start",
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    color: C.muted,
    margin: "0 0 6px",
  },
  value: { fontSize: 15, color: C.ink, margin: 0, lineHeight: 1.55 },
  link: { color: C.red, fontWeight: 600, textDecoration: "none" },
  actions: { display: "flex", gap: 18, alignItems: "center", flexWrap: "wrap", marginTop: 22 },
  h2: { fontSize: 28, fontWeight: 700, color: C.ink, margin: "0 0 8px", letterSpacing: "-0.3px" },
  h2sub: { fontSize: 15, color: C.muted, margin: "0 0 28px", lineHeight: 1.65 },
  termHead: {
    display: "flex",
    alignItems: "baseline",
    gap: 10,
    margin: "30px 0 12px",
    fontSize: 19,
    fontWeight: 700,
    color: C.ink,
  },
  now: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "1px",
    textTransform: "uppercase",
    color: "#fff",
    background: C.red,
    borderRadius: 3,
    padding: "2px 7px",
  },
  yearCard: { marginBottom: 16, overflowX: "auto" },
  yearHead: {
    background: C.sand,
    borderBottom: `1px solid ${C.line}`,
    padding: "11px 18px",
    fontSize: 15,
    fontWeight: 700,
    color: C.ink,
  },
  // fixed widths so every year's table lines up with the one above
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14, tableLayout: "fixed" },
  th: {
    textAlign: "left",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "1.2px",
    textTransform: "uppercase",
    color: C.muted,
    padding: "9px 18px",
    borderBottom: `1px solid ${C.line}`,
    whiteSpace: "nowrap",
  },
  td: { padding: "11px 18px", borderBottom: `1px solid ${C.line}`, color: C.body, verticalAlign: "top" },
  course: { fontWeight: 600, color: C.ink },
  muted: { color: C.muted, fontStyle: "italic" },
  note: {
    display: "inline-block",
    fontSize: 11.5,
    fontWeight: 600,
    color: "#92400e",
    background: "#fef3c7",
    borderRadius: 3,
    padding: "1px 7px",
  },
  empty: { padding: "22px 24px", fontSize: 15, lineHeight: 1.7, color: C.body },
  small: { fontSize: 12.5, color: C.muted, margin: "16px 0 0", lineHeight: 1.6 },
};

const levelName = (y) => {
  if (y === "" || y === null || y === undefined) return "Other classes";
  return Number(y) === 0 ? "Kindergarten" : `Year ${y}`;
};

const firstLesson = (d) =>
  d
    ? new Date(`${d}T00:00:00`).toLocaleDateString("en-AU", { weekday: "short", day: "numeric", month: "short" })
    : null;

const CampusPage = () => {
  const { campusSlug } = useParams();
  const [data, setData] = useState(undefined); // undefined: loading, null: not found

  useEffect(() => {
    let alive = true;
    setData(undefined);
    get_campus_timetable(campusSlug).then((d) => alive && setData(d));
    return () => {
      alive = false;
    };
  }, [campusSlug]);

  const campus = data?.campus;
  const terms = data?.terms || [];

  if (data === null) {
    return (
      <div style={{ background: "#fff" }}>
        <Header />
        <CommonBanner bannerHeading="Campus not found" path={[{ link: "/our-branches", name: "Campuses" }]} />
        <section style={s.section}>
          <div style={s.container}>
            <p style={s.value}>
              We couldn&rsquo;t find that campus.{" "}
              <Link style={s.link} to="/our-branches">
                See all campuses
              </Link>
            </p>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ background: "#fff" }}>
      <Header />
      <CommonBanner
        bannerHeading={campus?.name || "Campus"}
        subheading={campus ? [campus.region, campus.state].filter(Boolean).join(" · ") || undefined : undefined}
        path={[
          { link: "/our-branches", name: "Campuses" },
          ...(campus ? [{ link: `/campuses/${campus.slug}`, name: campus.name }] : []),
        ]}
      />

      {campus && (
        <section style={s.sectionAlt}>
          <div style={s.container}>
            <div style={s.info}>
              {campus.address && (
                <div>
                  <p style={s.label}>Address</p>
                  <p style={s.value}>
                    <a
                      style={{ color: "inherit", textDecoration: "none" }}
                      href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(campus.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Directions in Google Maps"
                    >
                      {campus.address}
                    </a>
                  </p>
                </div>
              )}
              {campus.phone && (
                <div>
                  <p style={s.label}>Phone</p>
                  <p style={s.value}>
                    <a style={s.link} href={`tel:${String(campus.phone).replace(/\s+/g, "")}`}>
                      {campus.phone}
                    </a>
                  </p>
                </div>
              )}
              {campus.email && (
                <div>
                  <p style={s.label}>Email</p>
                  <p style={s.value}>
                    <a style={s.link} href={`mailto:${campus.email}`}>
                      {campus.email}
                    </a>
                  </p>
                </div>
              )}
            </div>
            <div style={s.actions}>
              <a className="mq-btn" href={ENROL_URL}>
                Enrol now
              </a>
              <Link className="mq-link" to="/our-branches">
                All campuses
              </Link>
            </div>
          </div>
        </section>
      )}

      <section style={s.section}>
        <div style={s.container}>
          <h2 style={s.h2}>Timetable</h2>
          {data === undefined ? (
            <p style={s.h2sub}>Loading…</p>
          ) : terms.length === 0 ? (
            <div className="mq-card" style={s.empty}>
              The timetable for this term isn&rsquo;t published here yet.
              {campus?.phone ? (
                <>
                  {" "}Call the campus on{" "}
                  <a style={s.link} href={`tel:${String(campus.phone).replace(/\s+/g, "")}`}>
                    {campus.phone}
                  </a>{" "}
                  for class times.
                </>
              ) : (
                <>
                  {" "}
                  <Link style={s.link} to="/contact-us">
                    Contact us
                  </Link>{" "}
                  for class times.
                </>
              )}
            </div>
          ) : (
            <>
              <p style={s.h2sub}>
                Classes at {campus?.name}, by year level. Times are set by the campus and can change between terms.
              </p>
              {terms.map((t) => (
                <div key={t.term_id}>
                  <div style={s.termHead}>
                    {t.label}
                    {t.is_current ? <span style={s.now}>Now</span> : null}
                  </div>
                  {t.years.map((y) => (
                    <div key={`${t.term_id}-${y.year_level}`} className="mq-card" style={s.yearCard}>
                      <div style={s.yearHead}>{levelName(y.year_level)}</div>
                      <table style={s.table} className="cs-campus-tt">
                        <thead>
                          <tr>
                            <th style={{ ...s.th, width: "34%" }}>Course</th>
                            <th style={{ ...s.th, width: "17%" }}>Day</th>
                            <th style={{ ...s.th, width: "17%" }}>Time</th>
                            <th style={{ ...s.th, width: "20%" }}>First lesson</th>
                            <th style={{ ...s.th, width: "12%" }}>Weeks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {y.classes.map((cl) => (
                            <tr key={cl.id}>
                              <td style={s.td}>
                                <span style={s.course}>{cl.course}</span>
                                {cl.note ? (
                                  <>
                                    {" "}
                                    <span style={s.note}>{cl.note}</span>
                                  </>
                                ) : null}
                              </td>
                              <td style={s.td}>
                                {cl.day || <span style={s.muted}>{cl.session || "to be confirmed"}</span>}
                              </td>
                              <td style={s.td}>
                                {cl.start_time ? (
                                  `${cl.start_time}${cl.end_time ? ` – ${cl.end_time}` : ""}`
                                ) : (
                                  <span style={s.muted}>to be confirmed</span>
                                )}
                              </td>
                              <td style={s.td} className="tt-from">
                                {firstLesson(cl.first_lesson) || <span style={s.muted}>—</span>}
                              </td>
                              <td style={s.td} className="tt-weeks">
                                {cl.weeks || <span style={s.muted}>—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              ))}
              <p style={s.small}>
                Call the campus to check a place is free before enrolling. Fees are on the{" "}
                <Link style={s.link} to="/pricing">
                  pricing page
                </Link>
                .
              </p>
            </>
          )}
        </div>
      </section>

      <Footer />

      {/* On a phone each class is one block: the course, then "Tuesday ·
          16:40 – 18:00 · from Tue, 13 Oct · 10 weeks", instead of a table
          that has to be scrolled sideways. */}
      <style>{`
        @media (max-width: 640px) {
          .cs-campus-tt thead { display: none; }
          .cs-campus-tt, .cs-campus-tt tbody { display: block; }
          .cs-campus-tt tr { display: block; padding: 12px 16px; border-bottom: 1px solid ${C.line}; }
          .cs-campus-tt td { display: inline; padding: 0 !important; border: none !important; }
          .cs-campus-tt td:first-child { display: block; margin-bottom: 3px; }
          .cs-campus-tt td:nth-child(n+3)::before { content: " · "; color: ${C.muted}; }
          .cs-campus-tt td.tt-from::before { content: " · from "; }
          .cs-campus-tt td.tt-weeks::after { content: " weeks"; }
        }
      `}</style>
    </div>
  );
};

export default CampusPage;
